import moment from "moment";

import { GeneratedAttachmentResponse } from "@/Common/Api/Attachments/Dto/Responses/GeneratedAttachmentResponse";
import { PdfPageDetails } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { BatchScanPage } from "@/Documents/Api/Dto/Response/BatchScanPage";
import { DocumentAnnotationLog } from "@/Documents/Api/Dto/Response/DocumentAnnotationLog";
import { DocumentUrlAndBool } from "@/Documents/Api/Dto/Response/DocumentUrlAndBool";
import { DocumentsHelper } from "@/Documents/Helpers/DocumentsHelper";
import { MedicalDocumentItem, MedicalDocumentItemDto } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { IDocumentActionHistory } from "../Medical/IDocumentActionHistory";

type AdditionalDetailsVisible = "AdditionalDetailsVisible";
type Clinicians = "Clinicians";
type SelectedClinicians = "SelectedClinicians";
type SelectedPatients = "SelectedPatients";
type DetailsLinkLabel = "DetailsLinkLabel";
type DetailsLinkImagePath = "DetailsLinkImagePath";
type Note = "Note";
type Source = "Source";
type Description = "Description";
type AccompanyingMnemonic = "AccompanyingMnemonic";
type AccompanyingSubClass = "AccompanyingSubClass";
type SendingFacilityId = "SendingFacilityId";
type SendingFacilityReport = "SendingFacilityReport";
type ClassLookupId = "ClassLookupId";
type SubClassLookupId = "SubClassLookupId";
type HRMStatusList = "HRMStatusList";
type SelectedHRMStatus = "SelectedHRMStatus";
type NumberOfFiles = "NumberOfFiles";
type LockUI = "LockUI";
type MedicalDocumentInfo = "MedicalDocumentInfo";
type DocumentDate = "DocumentDate";
type AuthoringDate = "AuthoringDate";
type ObservationDate = "ObservationDate";
type IsUrgent = "IsUrgent";
type SignOffDate = "SignOffDate";
type SignOffByContactId = "SignOffByContactId";
type LastModifiedBy = "LastModifiedBy";
type LastModifiedByDate = "LastModifiedByDate";
type ShowNoteCreateForm = "ShowNoteCreateForm";
type RememberClinician = "RememberClinician";
type RememberDescription = "RememberDescription";
type RememberPatient = "RememberPatient";
type RememberSite = "RememberSite";
type StartNewPojectPopup = "StartNewPojectPopup";
type BatchScanPopupOpen = "BatchScanPopupOpen";
type PleaseWaitPopup = "PleaseWaitPopup";
type HideBatchScanAndAttachButton = "HideBatchScanAndAttachButton";
type SelectedBatchScanPages = "SelectedBatchScanPages";
type DeleteWarningPopup = "DeleteWarningPopup";
type CompleteBatchScan = "CompleteBatchScan";
type UnapprovePopupOpen = "UnapprovePopupOpen";
type Files = "Files";
type RevokedByContactId = "RevokedByContactId";
type RevokedOn = "RevokedOn";
type IsSaving = "IsSaving";
type ExternalAttachment = "ExternalAttachment";
type BatchScanPages = "BatchScanPages";
type ShowCreatePatientPopup = "ShowCreatePatientPopup";
type SelectedPageArray = "SelectedPageArray";
type SelectedPageTotalKb = "SelectedPageTotalKb";
type ListOfImagePath = "ListOfImagePath";
type ImageBase64List = "ImageBase64List";
type HasBatchScan = "HasBatchScan";
type DocumentAnnotationsLog = "DocumentAnnotationsLog";
type SourceAuthorName = "SourceAuthorName";
type DocumentActionHistoryLog = "DocumentActionHistoryLog";

interface IDocumentCreateEditUIModel {

    get(key: AdditionalDetailsVisible): boolean;
    set(key: AdditionalDetailsVisible, value: boolean): this;

    get(key: Clinicians): Immutable.List<Person>;
    set(key: Clinicians, value: Immutable.List<Person>): this;

    get(key: SelectedClinicians): Immutable.List<Person>;
    set(key: SelectedClinicians, value: Immutable.List<Person>): this;

    get(key: SelectedPatients): Immutable.List<Person>;
    set(key: SelectedPatients, value: Immutable.List<Person>): this;

    get(key: DetailsLinkLabel): string;
    set(key: DetailsLinkLabel, value: string): this;

    get(key: DetailsLinkImagePath): string;
    set(key: DetailsLinkImagePath, value: string): this;

    get(key: Note): string;
    set(key: Note, value: string): this;

    get(key: Source): string;
    set(key: Source, value: string): this;

    get(key: Description): string;
    set(key: Description, value: string): this;

    get(key: AccompanyingMnemonic): string;
    set(key: AccompanyingMnemonic, value: string): this;

    get(key: AccompanyingSubClass): string;
    set(key: AccompanyingSubClass, value: string): this;

    get(key: SourceAuthorName): string;
    set(key: SourceAuthorName, value: string): this;

    get(key: SendingFacilityId): string;
    set(key: SendingFacilityId, value: string): this;

    get(key: SendingFacilityReport): string;
    set(key: SendingFacilityReport, value: string): this;

    get(key: ClassLookupId): string;
    set(key: ClassLookupId, value: string): this;

    get(key: SubClassLookupId): string;
    set(key: SubClassLookupId, value: string): this;

    get(key: HRMStatusList): Immutable.List<KeyValuePair<string, string>>;
    set(key: HRMStatusList, value: Immutable.List<KeyValuePair<string, string>>): this;

    get(key: SelectedHRMStatus): string;
    set(key: SelectedHRMStatus, value: string): this;

    get(key: NumberOfFiles): number;
    set(key: NumberOfFiles, value: number): this;

    get(key: LockUI): boolean;
    set(key: LockUI, value: boolean): this;

    get(key: MedicalDocumentInfo): MedicalDocumentItem;
    set(key: MedicalDocumentInfo, value: MedicalDocumentItem): this;
    delete(key: MedicalDocumentInfo): this;

    get(key: DocumentDate): Date;
    set(key: DocumentDate, value: Date): this;

    get(key: AuthoringDate): Date;
    set(key: AuthoringDate, value: Date): this;

    get(key: ObservationDate): Date;
    set(key: ObservationDate, value: Date): this;

    get(key: IsUrgent): boolean;
    set(key: IsUrgent, value: boolean): this;

    get(key: SignOffDate): Date;
    set(key: SignOffDate, value: Date): this;

    get(key: SignOffByContactId): string;
    set(key: SignOffByContactId, value: string): this;

    get(key: LastModifiedBy): string;
    set(key: LastModifiedBy, value: string): this;

    get(key: LastModifiedByDate): Date;
    set(key: LastModifiedByDate, value: Date): this;

    get(key: ShowNoteCreateForm): boolean;
    set(key: ShowNoteCreateForm, value: boolean): this;

    get(key: RememberClinician): boolean;
    set(key: RememberClinician, value: boolean): this;

    get(key: RememberDescription): boolean;
    set(key: RememberDescription, value: boolean): this;

    get(key: RememberSite): boolean;
    set(key: RememberSite, value: boolean): this;

    get(key: RememberPatient): boolean;
    set(key: RememberPatient, value: boolean): this;

    get(key: StartNewPojectPopup): boolean;
    set(key: StartNewPojectPopup, value: boolean): this;

    get(key: BatchScanPopupOpen): boolean;
    set(key: BatchScanPopupOpen, value: boolean): this;

    get(key: PleaseWaitPopup): boolean;
    set(key: PleaseWaitPopup, value: boolean): this;

    get(key: HideBatchScanAndAttachButton): boolean;
    set(key: HideBatchScanAndAttachButton, value: boolean): this;

    get(key: SelectedBatchScanPages): PdfPageDetails[];
    set(key: SelectedBatchScanPages, value: PdfPageDetails[]): this;

    get(key: DeleteWarningPopup): boolean;
    set(key: DeleteWarningPopup, value: boolean): this;

    get(key: CompleteBatchScan): boolean;
    set(key: CompleteBatchScan, value: boolean): this;

    get(key: UnapprovePopupOpen): boolean;
    set(key: UnapprovePopupOpen, value: boolean): this;

    get(key: Files): File[];
    set(key: Files, value: File[]): this;

    get(key: RevokedByContactId): string;
    set(key: RevokedByContactId, value: string): this;

    get(key: RevokedOn): Date;
    set(key: RevokedOn, value: Date): this;

    get(key: IsSaving): boolean;
    set(key: IsSaving, value: boolean): this;

    get(key: ExternalAttachment): GeneratedAttachmentResponse;
    set(key: ExternalAttachment, value: GeneratedAttachmentResponse): this;

    get(key: BatchScanPages): BatchScanPage[];
    set(key: BatchScanPages, value: BatchScanPage[]): this;

    get(key: ShowCreatePatientPopup): boolean;
    set(key: ShowCreatePatientPopup, value: boolean): this;

    get(key: SelectedPageArray): Immutable.List<number>;
    set(key: SelectedPageArray, value: Immutable.List<number>): this;

    get(key: SelectedPageTotalKb): number;
    set(key: SelectedPageTotalKb, value: number): this;

    get(key: ListOfImagePath): Immutable.List<string>;
    set(key: ListOfImagePath, value: Immutable.List<string>): this;

    get(key: ImageBase64List): Immutable.List<DocumentUrlAndBool>;
    set(key: ImageBase64List, value: Immutable.List<DocumentUrlAndBool>): this;

    get(key: HasBatchScan): boolean;
    set(key: HasBatchScan, value: boolean): this;

    get(key: DocumentAnnotationsLog): DocumentAnnotationLog[];
    set(key: DocumentAnnotationsLog, value: DocumentAnnotationLog[]): this;

    get(key: DocumentActionHistoryLog): IDocumentActionHistory[];
    set(key: DocumentActionHistoryLog, value: IDocumentActionHistory[]): this;
}

class DocumentCreateEditUIModel {
    public static generateDocumentViewFooter(employeeContactId: string, type: string, typeDate: Date): string {
        let clinicianName: string;
        let clinician = cacheStore.CliniciansWithInactive
            .filter((c: Person) => c.ContactId === employeeContactId);

        if (clinician.length > 0)
            clinicianName = clinician[0].NameWithPrefix;

        return type +
            " " +
            clinicianName +
            " " +
            cacheStore.GetString("OnDateLabel") +
            " " +
            moment(typeDate).format("YYYY/MM/D") +
            " " +
            cacheStore.GetString("AtLabel") +
            " " +
            moment(typeDate).format("HH:mm:ss");
    }

    public static fromResponseModel(medicalDocumentListItemResponse: MedicalDocumentItemDto): IDocumentCreateEditUIModel {
        if (!medicalDocumentListItemResponse)
            return null;

        let selectedDocument: IDocumentCreateEditUIModel = Immutable.fromJS(DocumentsHelper.DefaultModelForDocumentCreateEditUIModel());
        selectedDocument = selectedDocument.set("MedicalDocumentInfo", new MedicalDocumentItem(medicalDocumentListItemResponse));

        if (!selectedDocument.get("MedicalDocumentInfo").Model.SignOffDate || true) {
            const selectedDocumentPatients = Immutable.List<Person>(
                medicalDocumentListItemResponse.Patients
                    .map(p => Person.FromIndividualLight(p))
            );

            const selectedDocumentClinicians = medicalDocumentListItemResponse.ResponsibleProviders
                .map(resposibleProviders => cacheStore.GetClinicians(true).firstOrDefault(p => p.ContactId === resposibleProviders.Id));

            selectedDocument = selectedDocument.set("SelectedPatients", selectedDocumentPatients)
                .set("Clinicians", Immutable.List(cacheStore.CliniciansWithInactive))
                .set("SelectedClinicians", Immutable.List<Person>(selectedDocumentClinicians));
        }
        const kvpList = [
            new KeyValuePair<string, string>(GuidHelper.Empty(), ""),
            ...cacheStore.GetHRMStatusList()
        ];

        selectedDocument = selectedDocument.set("AdditionalDetailsVisible", false)
            .set("ClassLookupId", medicalDocumentListItemResponse.ClassId)
            .set("SubClassLookupId", medicalDocumentListItemResponse.SubClassId)
            .set("SendingFacilityId", medicalDocumentListItemResponse.SendingFacilityID)
            .set("SendingFacilityReport", medicalDocumentListItemResponse.SendingFacilityReport)
            .set("AccompanyingMnemonic", medicalDocumentListItemResponse.AccompanyingMnemonic)
            .set("AccompanyingSubClass", medicalDocumentListItemResponse.AccompanyingSubClass)
            .set("SourceAuthorName", medicalDocumentListItemResponse.SourceAuthorName)
            .set("AuthoringDate", medicalDocumentListItemResponse.AuthoringDateTime)
            .set("ObservationDate", medicalDocumentListItemResponse.ObservationDateTime)

            .set("LastModifiedBy", medicalDocumentListItemResponse.LastModifiedById)
            .set("LastModifiedByDate", medicalDocumentListItemResponse.LastModifiedByDate)

            .set("SignOffDate", medicalDocumentListItemResponse.SignOffDate)
            .set("SignOffByContactId", medicalDocumentListItemResponse.SignOffByContactId)

            .set("RevokedByContactId", medicalDocumentListItemResponse.RevokedByContactId)
            .set("RevokedOn", medicalDocumentListItemResponse.RevokedOn)
            .set("DocumentAnnotationsLog", medicalDocumentListItemResponse.DocumentAnnotationsLog)

            .set("HRMStatusList", Immutable.List(kvpList))
            .set("SelectedHRMStatus", medicalDocumentListItemResponse.HRMStatusLookupId)
            .set("DocumentActionHistoryLog", medicalDocumentListItemResponse.DocumentActionHistory);

        return selectedDocument;
    }
}

interface IDocumentInformation {
    referenceId: string;
    target: number;
    subReferenceId: string;
    note: string;
    source: string;
    docClass: string;
    docSubClass: string;
    description: string;
    date: Date;
    documentId: string;
    isApproved: boolean;
    responsibleProvidersIds: string[];
    prescriptionRenewalId: Guid
}

export { DocumentCreateEditUIModel, IDocumentCreateEditUIModel, IDocumentInformation };
